import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Spinner } from '@edgebox/react-components';
import { useNavigate, useParams } from 'react-router-dom';
import { InternalId } from '@edgebox/data-definition-kit';
import { AiPromptExecutionStatus } from '@edgebox/sync-core-data-definitions';
import { ClientAiPromptExecutionEntity } from '@edgebox/sync-core-rest-client';
import { formatDateTime, formatMoney, renderValue, useSyncCoreApi } from '../aiHelpers';
import { AiModelName } from '../components/AiModelName';
import { AiPromptTemplateReferenceName } from '../components/AiPromptTemplateReferenceName';
import { AiProviderName } from '../components/AiProviderName';
import { DetailsCard } from '../components/DetailsCard';
import { ErrorCard } from '../components/ErrorCard';
import { LoadingCard } from '../components/LoadingCard';
import { RawDataCard } from '../components/RawDataCard';
import { ScreenHeader } from '../components/ScreenHeader';

function isExecutionRunning(status: AiPromptExecutionStatus): boolean {
  return status === AiPromptExecutionStatus.RunningSync || status === AiPromptExecutionStatus.RunningAsync;
}

function executionStatusVariant(status: AiPromptExecutionStatus): 'success' | 'primary' | 'danger' | 'secondary' {
  if (status === AiPromptExecutionStatus.Succeeded) return 'success';
  if (isExecutionRunning(status)) return 'primary';
  if (status === AiPromptExecutionStatus.Failed) return 'danger';
  return 'secondary';
}

export function AiPromptExecutionDetailView() {
  const api = useSyncCoreApi();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id as InternalId | undefined;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [entity, setEntity] = useState<ClientAiPromptExecutionEntity | undefined>(undefined);
  const [inputLogContent, setInputLogContent] = useState<string | undefined>(undefined);
  const [outputLogContent, setOutputLogContent] = useState<string | undefined>(undefined);
  const [inputLogLoading, setInputLogLoading] = useState(false);
  const [outputLogLoading, setOutputLogLoading] = useState(false);
  const [inputLogError, setInputLogError] = useState<string | undefined>(undefined);
  const [outputLogError, setOutputLogError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!api || !id) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    api.ai.promptExecutions
      .item(id)
      .then((e) => {
        if (cancelled) return;
        setEntity(e);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, id]);

  useEffect(() => {
    setInputLogContent(undefined);
    setOutputLogContent(undefined);
    setInputLogLoading(false);
    setOutputLogLoading(false);
    setInputLogError(undefined);
    setOutputLogError(undefined);
  }, [entity?.id]);

  const loadLogContent = async (kind: 'input' | 'output') => {
    const fileReference = kind === 'input' ? entity?.inputLogFile : entity?.outputLogFile;
    if (!fileReference?.get) {
      if (kind === 'input') {
        setInputLogError('No input log file available for this execution.');
      } else {
        setOutputLogError('No output log file available for this execution.');
      }
      return;
    }

    if (kind === 'input') {
      setInputLogLoading(true);
      setInputLogError(undefined);
    } else {
      setOutputLogLoading(true);
      setOutputLogError(undefined);
    }

    try {
      const file = await fileReference.get();
      const downloadUrl = file?.downloadUrl;
      if (!downloadUrl) {
        if (kind === 'input') {
          setInputLogError('Input log file cannot be downloaded.');
        } else {
          setOutputLogError('Output log file cannot be downloaded.');
        }
        return;
      }

      const response = await fetch(downloadUrl);
      const content = await response.text();

      if (kind === 'input') {
        setInputLogContent(content);
      } else {
        setOutputLogContent(content);
      }
    } catch {
      if (kind === 'input') {
        setInputLogError('Failed to load input log file.');
      } else {
        setOutputLogError('Failed to load output log file.');
      }
    } finally {
      if (kind === 'input') {
        setInputLogLoading(false);
      } else {
        setOutputLogLoading(false);
      }
    }
  };

  if (!id) return <Alert variant="warning">Missing prompt execution id.</Alert>;

  return (
    <>
      <ScreenHeader title="Prompt execution details" onBack={() => navigate('/ai/prompt-execution')} />

      {loading ? <LoadingCard text="Loading execution…" /> : null}
      {error ? <ErrorCard error={error} /> : null}

      {entity ? (
        <>
          <DetailsCard
            title="Overview"
            fields={[
              { label: 'ID', value: renderValue(entity.id) },
              {
                label: 'Status',
                value: <Badge bg={executionStatusVariant(entity.status)}>{entity.status}</Badge>,
              },
              { label: 'Created', value: renderValue(formatDateTime(entity.createdAt)) },
              { label: 'Started', value: renderValue(formatDateTime(entity.startedAt)) },
              { label: 'Completed', value: renderValue(formatDateTime(entity.completedAt)) },
              { label: 'Duration (ms)', value: renderValue(entity.durationMs) },
              { label: 'Is batched', value: renderValue(entity.isBatched) },
              { label: 'Retry count', value: renderValue(entity.retryCount) },
              { label: 'Provider', value: <AiProviderName id={entity.provider?.getId?.()} /> },
              { label: 'Model', value: <AiModelName id={entity.model?.getId?.()} /> },
              { label: 'Template', value: <AiPromptTemplateReferenceName templateKey={entity.promptTemplate?.getStableKey?.()} /> },
              { label: 'Customer', value: renderValue(entity.customer?.getId?.()) },
              { label: 'Project', value: renderValue(entity.project?.getId?.()) },
            ]}
          />

          <DetailsCard
            title="Usage"
            fields={[
              { label: 'Input tokens', value: renderValue(entity.usage?.inputTokens) },
              { label: 'Output tokens', value: renderValue(entity.usage?.outputTokens) },
              { label: 'Total tokens', value: renderValue(entity.usage?.tokens) },
              {
                label: 'Costs',
                value: renderValue(entity.usage?.costs !== undefined ? formatMoney(entity.usage.costs) : '-'),
              },
            ]}
          />

          <DetailsCard
            title="Logs"
            fields={[
              { label: 'Input log file', value: renderValue(entity.inputLogFile?.getId?.()) },
              { label: 'Output log file', value: renderValue(entity.outputLogFile?.getId?.()) },
              { label: 'Previous attempt', value: renderValue(entity.previousAttempt?.getId?.()) },
              { label: 'Output fix execution', value: renderValue(entity.outputFixExecution?.getId?.()) },
            ]}
          />

          <Card className="mb-3">
            <CardHeader className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <span>Log files</span>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void loadLogContent('input')}
                  disabled={inputLogLoading || !entity.inputLogFile?.get}
                >
                  {inputLogLoading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Loading input...
                    </>
                  ) : (
                    'Load input log'
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void loadLogContent('output')}
                  disabled={outputLogLoading || !entity.outputLogFile?.get}
                >
                  {outputLogLoading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" className="me-2" />
                      Loading output...
                    </>
                  ) : (
                    'Load output log'
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardBody className="d-flex flex-column gap-3">
              <div>
                <div className="fw-bold mb-2">Input log</div>
                {inputLogError ? (
                  <Alert variant="warning" className="mb-2">
                    {inputLogError}
                  </Alert>
                ) : null}
                {inputLogContent !== undefined ? (
                  <pre className="mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {inputLogContent}
                  </pre>
                ) : (
                  <div className="text-muted">Load input log to view raw content.</div>
                )}
              </div>

              <div>
                <div className="fw-bold mb-2">Output log</div>
                {outputLogError ? (
                  <Alert variant="warning" className="mb-2">
                    {outputLogError}
                  </Alert>
                ) : null}
                {outputLogContent !== undefined ? (
                  <pre className="mb-0 p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {outputLogContent}
                  </pre>
                ) : (
                  <div className="text-muted">Load output log to view raw content.</div>
                )}
              </div>
            </CardBody>
          </Card>

          <RawDataCard entity={entity} />
        </>
      ) : null}
    </>
  );
}
